/**
 * Runtime configuration that differs between web and native builds.
 *
 * Web: values come from `<meta>` tags that Rails renders in
 * `spa.html.erb` (stripe-key, current-user, jwt-token, csrf-token).
 *
 * Native: meta tags do not exist because there is no Rails-rendered layout.
 * Values come from Vite's `import.meta.env.VITE_*` (baked at build time) or
 * from the Rails API at runtime (e.g. `/api/v1/auth/session`).
 *
 * This module is the single read point for those values so screens never
 * do `document.querySelector('meta[name=...]')` directly.
 */

import { isNative } from './index'

function readMeta(name: string): string {
  if (typeof document === 'undefined') return ''
  const el = document.querySelector<HTMLMetaElement>(`meta[name="${name}"]`)
  return el?.content ?? ''
}

function readEnv(name: string): string {
  // Vite exposes `import.meta.env.*` values that start with `VITE_`.
  // Using a guarded `any` cast so this file stays jsdom/test-safe.
  const env = (import.meta as unknown as { env?: Record<string, string | undefined> }).env
  return env?.[name] ?? ''
}

export function stripePublishableKey(): string {
  if (isNative()) {
    return readEnv('VITE_STRIPE_PUBLISHABLE_KEY')
  }
  // Web: prefer the server-rendered meta tag, but fall back to the env var
  // when the app is run outside the Rails layout (e.g. the Capacitor build
  // served through `file://`).
  return readMeta('stripe-key') || readEnv('VITE_STRIPE_PUBLISHABLE_KEY')
}

export function apiBaseUrl(): string {
  if (isNative()) {
    // Native builds talk to the public Rails API; configured at build time.
    return readEnv('VITE_API_BASE_URL')
  }
  // Web builds are same-origin.
  return ''
}

// Absolute on native, same-origin on web. Every `fetch` to the Rails API must
// go through this: Capacitor sets no `server.url`, so the WebView origin is
// `https://localhost` and a bare `/api/...` path resolves against the WebView
// itself, where no Rails exists.
//
// One function rather than a copy in each caller — there were 21 identical
// local copies, which is 21 places for it to drift and no single place to test.
export function apiUrl(path: string): string {
  return `${apiBaseUrl()}${path}`
}

export function csrfToken(): string {
  if (isNative()) return ''
  return readMeta('csrf-token')
}

// Resolve a server media path (e.g. Active Storage `/rails/active_storage/...`)
// to a loadable URL. Relative paths break in the native WebView (they resolve
// against `https://localhost`), so prefix them with the API base on native.
export function mediaUrl(url: string | null | undefined): string {
  if (!url) return ''
  if (/^(https?:|data:|blob:)/.test(url)) return url
  return `${apiBaseUrl()}${url}`
}

export function currentUserMeta(): string {
  if (isNative()) return ''
  return readMeta('current-user')
}

export function jwtTokenMeta(): string {
  if (isNative()) return ''
  return readMeta('jwt-token')
}
